import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { FileText, Download, Eye, Trash2, Plus, Leaf, Coins, Activity, Shield, TrendingUp, CheckCircle2, X, ChevronRight, ChevronLeft, Loader2, FileSpreadsheet, File, Clock } from 'lucide-react';
// --- Types ---
interface Report {
  id: string;
  name: string;
  type: string;
  date: string;
  period: string;
  format: string;
  status: 'Completato' | 'In corso' | 'Errore' | 'Pianificato';
}
// --- Mock Data ---
const INITIAL_REPORTS: Report[] = [{
  id: '1',
  name: 'Report ESG Q1 2026',
  type: 'ESG',
  date: '25 Mar 2026',
  period: 'Gen-Mar 2026',
  format: 'PDF',
  status: 'Completato'
}, {
  id: '2',
  name: 'Analisi Costi Marzo',
  type: 'Costi',
  date: '28 Mar 2026',
  period: 'Mar 2026',
  format: 'Excel',
  status: 'Completato'
}, {
  id: '3',
  name: 'Utilizzo Servizi Q1',
  type: 'Utilizzo Servizi',
  date: '29 Mar 2026',
  period: 'Gen-Mar 2026',
  format: 'PDF',
  status: 'In corso'
}, {
  id: '4',
  name: 'Compliance AI Act Annual',
  type: 'Compliance AI Act',
  date: '20 Mar 2026',
  period: '2025',
  format: 'PDF',
  status: 'Completato'
}, {
  id: '5',
  name: 'Report GDPR Trimestrale',
  type: 'GDPR',
  date: '15 Mar 2026',
  period: 'Gen-Mar 2026',
  format: 'PDF',
  status: 'Completato'
}, {
  id: '6',
  name: 'Performance Mensile Feb',
  type: 'Performance',
  date: '01 Mar 2026',
  period: 'Feb 2026',
  format: 'CSV',
  status: 'Completato'
}, {
  id: '7',
  name: 'Report Costi Febbraio',
  type: 'Costi',
  date: '28 Feb 2026',
  period: 'Feb 2026',
  format: 'Excel',
  status: 'Completato'
}, {
  id: '8',
  name: 'ESG Annual 2025',
  type: 'ESG',
  date: '15 Gen 2026',
  period: '2025',
  format: 'PDF',
  status: 'Completato'
}];
const REPORT_TYPES = [{
  id: 'ESG',
  icon: Leaf,
  title: 'ESG',
  desc: "Impatto ambientale e sostenibilità dell'infrastruttura AI"
}, {
  id: 'Costi',
  icon: Coins,
  title: 'Costi',
  desc: 'Analisi dettagliata dei costi per servizio e provider'
}, {
  id: 'Utilizzo Servizi',
  icon: Activity,
  title: 'Utilizzo Servizi',
  desc: 'Metriche di utilizzo, volumi e performance dei servizi'
}, {
  id: 'Compliance AI Act',
  icon: Shield,
  title: 'Compliance AI Act',
  desc: 'Stato di conformità rispetto al regolamento EU AI Act'
}, {
  id: 'GDPR',
  icon: FileText,
  title: 'GDPR',
  desc: 'Conformità al trattamento dati personali nei servizi AI'
}, {
  id: 'Performance',
  icon: TrendingUp,
  title: 'Performance',
  desc: 'Analisi delle performance e tempi di risposta'
}];
const THEMES_BY_TYPE: Record<string, string[]> = {
  ESG: ['Emissioni CO₂', 'Consumo energetico', 'Confronto provider', 'Suggerimenti ottimizzazione', 'Trend storico'],
  Costi: ['Breakdown per servizio', 'Breakdown per provider', 'Anomalie rilevate', 'Previsioni', 'Confronto periodi'],
  'Utilizzo Servizi': ['Volumi di richieste', 'Utenti attivi', 'Error rate', 'Latenza media', 'Distribuzione oraria'],
  'Compliance AI Act': ['Classificazione rischio', 'Trasparenza', 'Sorveglianza umana', 'Robustezza tecnica', 'Documentazione'],
  GDPR: ['Trattamento PII', 'Ritenzione dati', "Diritto all'oblio", 'Consensi', 'Data Breach'],
  Performance: ['Tempo di risposta', 'Throughput', 'Uptime', 'Errori per modello', 'Degrado prestazioni']
};
const SERVICES = ['Customer Assistant', 'Smart Matcher', 'Content Moderator', 'AI Tutor'];
const FORMAT_OPTIONS = [{
  id: 'PDF',
  icon: File,
  label: 'PDF',
  desc: 'Documento formattato, ideale per condivisione'
}, {
  id: 'Excel',
  icon: FileSpreadsheet,
  label: 'Excel',
  desc: 'Foglio di calcolo con dati dettagliati'
}, {
  id: 'CSV',
  icon: FileText,
  label: 'CSV',
  desc: 'Dati grezzi per analisi personalizzate'
}];
// --- Helpers ---
function getStatusBadge(status: string) {
  switch (status) {
    case 'Completato':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-4222">
          Completato
        </span>;
    case 'In corso':
      return <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200" data-id="element-4223">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" data-id="element-4224" />
          In corso
        </span>;
    case 'Errore':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 border border-red-200" data-id="element-4225">
          Errore
        </span>;
    case 'Pianificato':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-4226">
          Pianificato
        </span>;
    default:
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-gray-50 text-gray-700 border border-gray-200" data-id="element-4227">
          {status}
        </span>;
  }
}
function getTypeBadge(type: string) {
  const colors: Record<string, string> = {
    ESG: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Costi: 'bg-blue-50 text-blue-700 border-blue-200',
    'Utilizzo Servizi': 'bg-purple-50 text-purple-700 border-purple-200',
    'Compliance AI Act': 'bg-amber-50 text-amber-700 border-amber-200',
    GDPR: 'bg-red-50 text-red-700 border-red-200',
    Performance: 'bg-cyan-50 text-cyan-700 border-cyan-200'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${colors[type] || 'bg-gray-50 text-gray-700 border-gray-200'}`} data-id="element-4228">
      {type}
    </span>;
}
// --- Report content helpers ---
function getReportSummary(type: string): string {
  const summaries: Record<string, string> = {
    ESG: "L'infrastruttura AI ha generato 124 kg di CO₂ nel periodo analizzato, con una riduzione del 15% rispetto al trimestre precedente. Il routing dinamico verso modelli SLM ha contribuito al risparmio di 45 kg di emissioni. Si raccomanda l'adozione di provider con data center alimentati da energie rinnovabili per un ulteriore miglioramento.",
    Costi: 'La spesa complessiva per i servizi AI nel periodo è stata di €2.435, con un incremento del 17% rispetto alla media. Il servizio Customer Assistant rappresenta il 42% della spesa totale. Sono state identificate opportunità di ottimizzazione per un risparmio stimato di €380/mese attraverso il passaggio a modelli più efficienti.',
    GDPR: "L'analisi di conformità GDPR evidenzia un livello di compliance del 96%. Tutti i servizi implementano correttamente il mascheramento PII. Sono stati identificati 2 punti di attenzione relativi alla ritenzione dei dati di conversazione che superano il periodo previsto dalla policy aziendale.",
    'Compliance AI Act': 'La valutazione di conformità al regolamento EU AI Act classifica tutti i servizi attivi come rischio limitato o minimo. Il servizio Customer Assistant richiede documentazione aggiuntiva sulla trasparenza. Il framework di sorveglianza umana è implementato correttamente su tutti i servizi ad alto volume.',
    Performance: "Le performance complessive dei servizi AI mostrano un uptime del 99.7% nel periodo analizzato. Il tempo medio di risposta è di 1.2 secondi, con picchi di 3.8 secondi nelle ore di punta. Il servizio Smart Matcher ha registrato un degrado del 12% nelle performance durante l'ultimo aggiornamento del modello.",
    'Utilizzo Servizi': "Nel periodo analizzato, i servizi AI hanno gestito complessivamente 512.000 richieste con un tasso di successo del 98.3%. Il servizio Customer Assistant è il più utilizzato con il 45% del volume totale. Si registra una crescita del 22% nell'utilizzo rispetto al periodo precedente."
  };
  return summaries[type] || summaries['Utilizzo Servizi'];
}
function getReportMetrics(type: string) {
  const m: Record<string, Array<{
    label: string;
    value: string;
    sub: string;
  }>> = {
    ESG: [{
      label: 'CO₂ Emessa',
      value: '124 kg',
      sub: '-15% vs Q4'
    }, {
      label: 'Energia Consumata',
      value: '890 kWh',
      sub: '-8% vs Q4'
    }, {
      label: 'CO₂ Risparmiata',
      value: '45 kg',
      sub: 'Routing SLM'
    }, {
      label: 'Eco Score',
      value: 'B+',
      sub: 'In miglioramento'
    }],
    Costi: [{
      label: 'Spesa Totale',
      value: '€2.435',
      sub: '+17% vs media'
    }, {
      label: 'Costo Medio/Req',
      value: '€0.0047',
      sub: '-3% vs Q4'
    }, {
      label: 'Servizio Top',
      value: 'Cust. Assist.',
      sub: '42% del totale'
    }, {
      label: 'Risparmio Possibile',
      value: '€380/m',
      sub: 'Ottimizzazione'
    }],
    GDPR: [{
      label: 'Compliance',
      value: '96%',
      sub: '+2% vs Q4'
    }, {
      label: 'PII Masked',
      value: '100%',
      sub: 'Tutti i servizi'
    }, {
      label: 'Punti Attenzione',
      value: '2',
      sub: 'Ritenzione dati'
    }, {
      label: 'Data Breach',
      value: '0',
      sub: 'Nessun incidente'
    }]
  };
  return m[type] || [{
    label: 'Richieste Totali',
    value: '512K',
    sub: '+22% vs prec.'
  }, {
    label: 'Tasso Successo',
    value: '98.3%',
    sub: 'Stabile'
  }, {
    label: 'Tempo Medio',
    value: '1.2s',
    sub: '-0.3s vs Q4'
  }, {
    label: 'Servizi Attivi',
    value: '4',
    sub: 'Tutti operativi'
  }];
}
function getServiceRows(type: string) {
  const valCol = type === 'ESG' ? 'CO₂ (kg)' : type === 'Costi' ? 'Costo (€)' : 'Richieste';
  const rows = [{
    name: 'Customer Assistant',
    val: type === 'ESG' ? '52' : type === 'Costi' ? '€1.024' : '230K',
    change: type === 'Costi' ? '+17%' : '-12%',
    status: type === 'Costi' ? 'Attenzione' : 'Nella norma'
  }, {
    name: 'Smart Matcher',
    val: type === 'ESG' ? '38' : type === 'Costi' ? '€680' : '142K',
    change: '-8%',
    status: 'Nella norma'
  }, {
    name: 'Content Moderator',
    val: type === 'ESG' ? '22' : type === 'Costi' ? '€456' : '98K',
    change: '-5%',
    status: 'Nella norma'
  }, {
    name: 'AI Tutor',
    val: type === 'ESG' ? '12' : type === 'Costi' ? '€275' : '42K',
    change: '-22%',
    status: 'Ottimale'
  }];
  return {
    valCol,
    rows
  };
}
function getRecommendations(type: string) {
  const recs: Record<string, string[]> = {
    ESG: ['Valutare la migrazione del servizio Customer Assistant verso un provider con data center alimentati da energie rinnovabili.', 'Estendere il routing dinamico SLM a tutti i servizi per massimizzare il risparmio energetico.', 'Pianificare una revisione trimestrale delle configurazioni per allinearsi alle best practice aggiornate.'],
    Costi: ['Passare il servizio Customer Assistant al modello GPT-4o-mini per le query semplici, con risparmio stimato del 40%.', 'Implementare budget alert automatici per prevenire sforamenti di spesa sui singoli servizi.', 'Pianificare una revisione trimestrale delle configurazioni per allinearsi alle best practice aggiornate.']
  };
  return recs[type] || ['Implementare un sistema di caching per le richieste ripetute, riducendo il carico del 15%.', 'Monitorare il degrado delle performance durante gli aggiornamenti dei modelli con rollback automatico.', 'Pianificare una revisione trimestrale delle configurazioni per allinearsi alle best practice aggiornate.'];
}
// --- A4 Page wrapper for PDF preview ---
function A4Page({
  children,
  pageNum,
  totalPages
}: {
  children: React.ReactNode;
  pageNum: number;
  totalPages: number;
}) {
  return <div className="bg-white shadow-lg border border-gray-200 mx-auto mb-6 relative" style={{
    width: '595px',
    minHeight: '842px',
    padding: '48px 56px 64px 56px'
  }} data-id="element-4229">
      {children}
      <div className="absolute bottom-6 left-0 right-0 flex items-center justify-between px-14" data-id="element-4230">
        <span className="text-[9px] text-gray-300" data-id="element-4231">
          Confidenziale — Uso interno
        </span>
        <span className="text-[9px] text-gray-400" data-id="element-4232">
          Pagina {pageNum} di {totalPages}
        </span>
      </div>
    </div>;
}
// --- Excel preview ---
function ExcelPreview({
  report
}: {
  report: Report;
}) {
  const {
    valCol,
    rows
  } = getServiceRows(report.type);
  const metrics = getReportMetrics(report.type);
  return <div className="bg-white border border-gray-300 rounded-lg overflow-hidden shadow-lg mx-auto" style={{
    maxWidth: '800px'
  }} data-id="element-4233">
      {/* Toolbar */}
      <div className="bg-[#217346] px-4 py-2 flex items-center gap-3" data-id="element-4234">
        <FileSpreadsheet className="w-4 h-4 text-white" data-id="element-4235" />
        <span className="text-[12px] text-white font-medium" data-id="element-4236">
          {report.name}.xlsx
        </span>
      </div>
      {/* Formula bar */}
      <div className="bg-gray-100 border-b border-gray-300 px-3 py-1.5 flex items-center gap-2" data-id="element-4237">
        <span className="text-[10px] font-mono text-gray-500 bg-white border border-gray-300 px-2 py-0.5 rounded" data-id="element-4238">
          A1
        </span>
        <span className="text-[10px] font-mono text-gray-600" data-id="element-4239">
          = {report.name}
        </span>
      </div>
      {/* Sheet content */}
      <div className="overflow-x-auto" data-id="element-4240">
        <table className="w-full border-collapse" data-id="element-4241">
          <thead data-id="element-4242">
            <tr className="bg-gray-50" data-id="element-4243">
              <th className="w-10 border border-gray-200 bg-gray-100 text-[9px] text-gray-500 text-center py-1" data-id="element-4244"></th>
              <th className="border border-gray-200 bg-gray-100 text-[10px] text-gray-600 font-medium text-center py-1 px-2 min-w-[120px]" data-id="element-4245">
                A
              </th>
              <th className="border border-gray-200 bg-gray-100 text-[10px] text-gray-600 font-medium text-center py-1 px-2 min-w-[100px]" data-id="element-4246">
                B
              </th>
              <th className="border border-gray-200 bg-gray-100 text-[10px] text-gray-600 font-medium text-center py-1 px-2 min-w-[100px]" data-id="element-4247">
                C
              </th>
              <th className="border border-gray-200 bg-gray-100 text-[10px] text-gray-600 font-medium text-center py-1 px-2 min-w-[100px]" data-id="element-4248">
                D
              </th>
              <th className="border border-gray-200 bg-gray-100 text-[10px] text-gray-600 font-medium text-center py-1 px-2 min-w-[100px]" data-id="element-4249">
                E
              </th>
            </tr>
          </thead>
          <tbody data-id="element-4250">
            {/* Title row */}
            <tr data-id="element-4251">
              <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4252">
                1
              </td>
              <td colSpan={5} className="border border-gray-200 py-2 px-3 text-[12px] font-bold text-[#1A202C] bg-blue-50" data-id="element-4253">
                {report.name}
              </td>
            </tr>
            <tr data-id="element-4254">
              <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4255">
                2
              </td>
              <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-500" data-id="element-4256">
                Periodo
              </td>
              <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-700" data-id="element-4257">
                {report.period}
              </td>
              <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-500" data-id="element-4258">
                Data
              </td>
              <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-700" data-id="element-4259">
                {report.date}
              </td>
              <td className="border border-gray-200" data-id="element-4260"></td>
            </tr>
            <tr data-id="element-4261">
              <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4262">
                3
              </td>
              <td colSpan={5} className="border border-gray-200" data-id="element-4263"></td>
            </tr>
            {/* Metrics header */}
            <tr data-id="element-4264">
              <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4265">
                4
              </td>
              <td colSpan={5} className="border border-gray-200 py-1.5 px-3 text-[11px] font-bold text-[#1A202C] bg-gray-50" data-id="element-4266">
                Metriche Principali
              </td>
            </tr>
            {metrics.map((m, i) => <tr key={i} data-id="element-4267">
                <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4268">
                  {5 + i}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-600" data-id="element-4269">
                  {m.label}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] font-medium text-[#1A202C]" data-id="element-4270">
                  {m.value}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-500" data-id="element-4271">
                  {m.sub}
                </td>
                <td className="border border-gray-200" data-id="element-4272"></td>
                <td className="border border-gray-200" data-id="element-4273"></td>
              </tr>)}
            <tr data-id="element-4274">
              <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4275">
                {9}
              </td>
              <td colSpan={5} className="border border-gray-200" data-id="element-4276"></td>
            </tr>
            {/* Service detail header */}
            <tr data-id="element-4277">
              <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4278">
                10
              </td>
              <td className="border border-gray-200 py-1.5 px-3 text-[10px] font-bold text-[#1A202C] bg-gray-50" data-id="element-4279">
                Servizio
              </td>
              <td className="border border-gray-200 py-1.5 px-3 text-[10px] font-bold text-[#1A202C] bg-gray-50" data-id="element-4280">
                {valCol}
              </td>
              <td className="border border-gray-200 py-1.5 px-3 text-[10px] font-bold text-[#1A202C] bg-gray-50" data-id="element-4281">
                Variazione
              </td>
              <td className="border border-gray-200 py-1.5 px-3 text-[10px] font-bold text-[#1A202C] bg-gray-50" data-id="element-4282">
                Stato
              </td>
              <td className="border border-gray-200 bg-gray-50" data-id="element-4283"></td>
            </tr>
            {rows.map((row, i) => <tr key={i} data-id="element-4284">
                <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4285">
                  {11 + i}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-700" data-id="element-4286">
                  {row.name}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-700" data-id="element-4287">
                  {row.val}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-700" data-id="element-4288">
                  {row.change}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-700" data-id="element-4289">
                  {row.status}
                </td>
                <td className="border border-gray-200" data-id="element-4290"></td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {/* Sheet tabs */}
      <div className="bg-gray-100 border-t border-gray-300 px-3 py-1.5 flex items-center gap-1" data-id="element-4291">
        <span className="text-[10px] font-medium text-[#217346] bg-white border border-gray-300 px-3 py-1 rounded-t" data-id="element-4292">
          Riepilogo
        </span>
        <span className="text-[10px] text-gray-500 bg-gray-50 border border-gray-200 px-3 py-1 rounded-t" data-id="element-4293">
          Dettaglio Servizi
        </span>
        <span className="text-[10px] text-gray-500 bg-gray-50 border border-gray-200 px-3 py-1 rounded-t" data-id="element-4294">
          Dati Grezzi
        </span>
      </div>
    </div>;
}
// --- CSV preview ---
function CsvPreview({
  report
}: {
  report: Report;
}) {
  const {
    valCol,
    rows
  } = getServiceRows(report.type);
  const metrics = getReportMetrics(report.type);
  const lines = [`# ${report.name}`, `# Periodo: ${report.period}`, `# Data: ${report.date}`, `# Tipo: ${report.type}`, '', '# Metriche Principali', 'Metrica,Valore,Note', ...metrics.map(m => `${m.label},${m.value},${m.sub}`), '', '# Dettaglio per Servizio', `Servizio,${valCol},Variazione,Stato`, ...rows.map(r => `${r.name},${r.val},${r.change},${r.status}`)];
  return <div className="bg-[#1e1e1e] rounded-lg overflow-hidden shadow-lg mx-auto border border-gray-700" style={{
    maxWidth: '700px'
  }} data-id="element-4295">
      <div className="bg-[#2d2d2d] px-4 py-2 flex items-center gap-2 border-b border-gray-700" data-id="element-4296">
        <FileText className="w-3.5 h-3.5 text-gray-400" data-id="element-4297" />
        <span className="text-[11px] text-gray-300 font-mono" data-id="element-4298">
          {report.name.replace(/\s+/g, '_').toLowerCase()}.csv
        </span>
      </div>
      <div className="p-4 overflow-x-auto" data-id="element-4299">
        <pre className="text-[11px] font-mono leading-relaxed" data-id="element-4300">
          {lines.map((line, i) => <div key={i} className="flex" data-id="element-4301">
              <span className="text-gray-600 w-8 text-right mr-4 select-none flex-shrink-0" data-id="element-4302">
                {i + 1}
              </span>
              <span className={line.startsWith('#') ? 'text-green-400' : line.includes(',') ? 'text-gray-300' : 'text-gray-500'} data-id="element-4303">
                {line}
              </span>
            </div>)}
        </pre>
      </div>
    </div>;
}
// --- PDF preview (A4 pages) ---
function PdfPreview({
  report
}: {
  report: Report;
}) {
  const summary = getReportSummary(report.type);
  const metrics = getReportMetrics(report.type);
  const {
    valCol,
    rows
  } = getServiceRows(report.type);
  const recs = getRecommendations(report.type);
  const typeIcon = REPORT_TYPES.find(rt => rt.id === report.type);
  return <div className="flex flex-col items-center" data-id="element-4304">
      {/* Page 1: Cover */}
      <A4Page pageNum={1} totalPages={3} data-id="element-4305">
        <div className="flex flex-col h-full justify-between" style={{
        minHeight: '700px'
      }} data-id="element-4306">
          <div data-id="element-4307">
            <div className="flex items-center justify-between mb-16" data-id="element-4308">
              <div className="flex items-center gap-2" data-id="element-4309">
                <div className="w-8 h-8 rounded bg-[#2A3B5C] flex items-center justify-center" data-id="element-4310">
                  <span className="text-white text-[10px] font-bold" data-id="element-4311">AI</span>
                </div>
                <span className="text-[11px] font-medium text-gray-400" data-id="element-4312">
                  AI Governance Platform
                </span>
              </div>
              <span className="text-[10px] text-gray-400" data-id="element-4313">CONFIDENZIALE</span>
            </div>
            <div className="mt-20" data-id="element-4314">
              <div className="w-12 h-1 bg-[#00B4D8] rounded mb-6" data-id="element-4315" />
              <p className="text-[11px] text-[#00B4D8] uppercase tracking-widest font-medium mb-3" data-id="element-4316">
                Report {report.type}
              </p>
              <h1 className="text-[28px] font-bold text-[#1A202C] leading-tight mb-4" data-id="element-4317">
                {report.name}
              </h1>
              <div className="w-16 h-0.5 bg-gray-200 mb-6" data-id="element-4318" />
              <div className="space-y-2 text-[12px] text-gray-500" data-id="element-4319">
                <p data-id="element-4320">
                  Periodo di riferimento:{' '}
                  <span className="text-[#1A202C] font-medium" data-id="element-4321">
                    {report.period}
                  </span>
                </p>
                <p data-id="element-4322">
                  Data di generazione:{' '}
                  <span className="text-[#1A202C] font-medium" data-id="element-4323">
                    {report.date}
                  </span>
                </p>
                <p data-id="element-4324">
                  Formato:{' '}
                  <span className="text-[#1A202C] font-medium" data-id="element-4325">
                    {report.format}
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4 mt-auto" data-id="element-4326">
            <p className="text-[10px] text-gray-400" data-id="element-4327">
              Generato automaticamente dalla piattaforma AI Governance · SIAE
            </p>
          </div>
        </div>
      </A4Page>

      {/* Page 2: Executive Summary + Metrics */}
      <A4Page pageNum={2} totalPages={3} data-id="element-4328">
        <div data-id="element-4329">
          <h2 className="text-[16px] font-bold text-[#1A202C] mb-1" data-id="element-4330">
            1. Executive Summary
          </h2>
          <div className="w-8 h-0.5 bg-[#00B4D8] rounded mb-4" data-id="element-4331" />
          <p className="text-[11px] text-gray-600 leading-[1.8] mb-8" data-id="element-4332">
            {summary}
          </p>

          <h2 className="text-[16px] font-bold text-[#1A202C] mb-1" data-id="element-4333">
            2. Metriche Principali
          </h2>
          <div className="w-8 h-0.5 bg-[#00B4D8] rounded mb-4" data-id="element-4334" />
          <div className="grid grid-cols-2 gap-3 mb-8" data-id="element-4335">
            {metrics.map((m, i) => <div key={i} className="border border-gray-200 rounded-lg p-3" data-id="element-4336">
                <p className="text-[9px] text-gray-400 uppercase tracking-wider mb-1" data-id="element-4337">
                  {m.label}
                </p>
                <p className="text-[18px] font-bold text-[#1A202C]" data-id="element-4338">
                  {m.value}
                </p>
                <p className="text-[9px] text-gray-400 mt-0.5" data-id="element-4339">{m.sub}</p>
              </div>)}
          </div>

          <h2 className="text-[16px] font-bold text-[#1A202C] mb-1" data-id="element-4340">
            3. Dettaglio per Servizio
          </h2>
          <div className="w-8 h-0.5 bg-[#00B4D8] rounded mb-4" data-id="element-4341" />
          <table className="w-full mb-4" data-id="element-4342">
            <thead data-id="element-4343">
              <tr className="border-b-2 border-gray-200" data-id="element-4344">
                <th className="text-left text-[9px] font-semibold text-gray-500 uppercase tracking-wider pb-2" data-id="element-4345">
                  Servizio
                </th>
                <th className="text-left text-[9px] font-semibold text-gray-500 uppercase tracking-wider pb-2" data-id="element-4346">
                  {valCol}
                </th>
                <th className="text-left text-[9px] font-semibold text-gray-500 uppercase tracking-wider pb-2" data-id="element-4347">
                  Var.
                </th>
                <th className="text-left text-[9px] font-semibold text-gray-500 uppercase tracking-wider pb-2" data-id="element-4348">
                  Stato
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4349">
              {rows.map((row, i) => <tr key={i} className="border-b border-gray-100" data-id="element-4350">
                  <td className="py-2 text-[10px] font-medium text-[#1A202C]" data-id="element-4351">
                    {row.name}
                  </td>
                  <td className="py-2 text-[10px] text-gray-600" data-id="element-4352">{row.val}</td>
                  <td className="py-2 text-[10px]" data-id="element-4353">
                    <span className={row.change.startsWith('+') ? 'text-red-600' : 'text-emerald-600'} data-id="element-4354">
                      {row.change}
                    </span>
                  </td>
                  <td className="py-2 text-[10px] text-gray-600" data-id="element-4355">
                    {row.status}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </A4Page>

      {/* Page 3: Recommendations + Footer */}
      <A4Page pageNum={3} totalPages={3} data-id="element-4356">
        <div data-id="element-4357">
          <h2 className="text-[16px] font-bold text-[#1A202C] mb-1" data-id="element-4358">
            4. Raccomandazioni
          </h2>
          <div className="w-8 h-0.5 bg-[#00B4D8] rounded mb-4" data-id="element-4359" />
          <div className="space-y-4 mb-12" data-id="element-4360">
            {recs.map((rec, i) => <div key={i} className="flex items-start gap-3" data-id="element-4361">
                <div className="w-5 h-5 rounded-full bg-[#00B4D8]/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-4362">
                  <span className="text-[9px] font-bold text-[#00B4D8]" data-id="element-4363">
                    {i + 1}
                  </span>
                </div>
                <p className="text-[11px] text-gray-600 leading-[1.8]" data-id="element-4364">{rec}</p>
              </div>)}
          </div>

          <h2 className="text-[16px] font-bold text-[#1A202C] mb-1" data-id="element-4365">
            5. Metodologia
          </h2>
          <div className="w-8 h-0.5 bg-[#00B4D8] rounded mb-4" data-id="element-4366" />
          <p className="text-[11px] text-gray-600 leading-[1.8] mb-8" data-id="element-4367">
            I dati presentati in questo report sono stati raccolti
            automaticamente dalla piattaforma AI Governance Engine. Le metriche
            sono calcolate sulla base dei log di utilizzo, dei costi fatturati
            dai provider e delle analisi di conformità eseguite in tempo reale.
            Le raccomandazioni sono generate attraverso l'analisi comparativa
            con le best practice di settore e i benchmark interni.
          </p>

          <div className="border-t border-gray-200 pt-6 mt-12" data-id="element-4368">
            <div className="flex items-center justify-between" data-id="element-4369">
              <div data-id="element-4370">
                <p className="text-[10px] text-gray-400 mb-1" data-id="element-4371">
                  Documento generato automaticamente
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-4372">
                  AI Governance Platform — SIAE © 2026
                </p>
              </div>
              <div className="text-right" data-id="element-4373">
                <p className="text-[10px] text-gray-400 mb-1" data-id="element-4374">
                  Classificazione: Confidenziale
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-4375">
                  Distribuzione: Uso interno
                </p>
              </div>
            </div>
          </div>
        </div>
      </A4Page>
    </div>;
}
// --- Report Preview Modal ---
function ReportPreviewModal({
  report,
  onClose
}: {
  report: Report;
  onClose: () => void;
}) {
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-4376">
      <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col shadow-2xl" data-id="element-4377">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0" data-id="element-4378">
          <div className="flex items-center gap-3" data-id="element-4379">
            <div className="w-9 h-9 rounded-lg bg-[#00B4D8]/10 flex items-center justify-center" data-id="element-4380">
              {report.format === 'Excel' ? <FileSpreadsheet className="w-4.5 h-4.5 text-[#217346]" data-id="element-4381" /> : report.format === 'CSV' ? <FileText className="w-4.5 h-4.5 text-gray-500" data-id="element-4382" /> : <File className="w-4.5 h-4.5 text-red-500" data-id="element-4383" />}
            </div>
            <div data-id="element-4384">
              <h2 className="text-[15px] font-heading font-semibold text-[#1A202C]" data-id="element-4385">
                {report.name}
              </h2>
              <div className="flex items-center gap-2 mt-0.5" data-id="element-4386">
                {getTypeBadge(report.type)}
                <span className="text-[10px] text-gray-400" data-id="element-4387">
                  {report.date} · {report.period}
                </span>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-gray-100 text-gray-600" data-id="element-4388">
                  {report.format}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-4389">
            <button className="flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium text-gray-600 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-all" data-id="element-4390">
              <Download className="w-3.5 h-3.5" data-id="element-4391" />
              Scarica
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all" data-id="element-4392">
              <X className="w-5 h-5" data-id="element-4393" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-gray-100 p-6" data-id="element-4394">
          {report.format === 'PDF' && <PdfPreview report={report} data-id="element-4395" />}
          {report.format === 'Excel' && <ExcelPreview report={report} data-id="element-4396" />}
          {report.format === 'CSV' && <CsvPreview report={report} data-id="element-4397" />}
        </div>
      </div>
    </div>;
}
// --- Main Component ---
export function ReportPage() {
  const [reports, setReports] = useState<Report[]>(INITIAL_REPORTS);
  // Preview modal state
  const [previewReport, setPreviewReport] = useState<Report | null>(null);
  // Wizard state
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  // Form state
  const [selectedType, setSelectedType] = useState('ESG');
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('PDF');
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);
  const handleOpenWizard = () => {
    setWizardStep(1);
    setSelectedType('ESG');
    setSelectedThemes(['Emissioni CO₂', 'Consumo energetico']);
    setDateFrom('');
    setDateTo('');
    setSelectedServices([...SERVICES]);
    setNotes('');
    setSelectedFormat('PDF');
    setIsGenerating(false);
    setProgress(0);
    setIsComplete(false);
    setIsWizardOpen(true);
  };
  const handleCloseWizard = () => {
    if (isComplete) {
      const newReport: Report = {
        id: Math.random().toString(36).substr(2, 9),
        name: `Nuovo Report ${selectedType}`,
        type: selectedType,
        date: new Date().toLocaleDateString('it-IT', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }),
        period: dateFrom && dateTo ? `${dateFrom} / ${dateTo}` : 'Mese Corrente',
        format: selectedFormat,
        status: 'Completato'
      };
      setReports(prev => [newReport, ...prev]);
    }
    setIsWizardOpen(false);
  };
  const toggleTheme = (theme: string) => {
    setSelectedThemes(prev => prev.includes(theme) ? prev.filter(t => t !== theme) : [...prev, theme]);
  };
  const toggleService = (service: string) => {
    setSelectedServices(prev => prev.includes(service) ? prev.filter(s => s !== service) : [...prev, service]);
  };
  const startGeneration = () => {
    setWizardStep(4);
    setIsGenerating(true);
    setProgress(0);
    setProgressMessage('Raccolta dati in corso...');
    const duration = 3000;
    const interval = 30;
    const steps = duration / interval;
    let currentStep = 0;
    timerRef.current = setInterval(() => {
      currentStep++;
      const p = Math.floor(currentStep / steps * 100);
      setProgress(p);
      if (p < 25) {
        setProgressMessage('Raccolta dati in corso...');
      } else if (p < 50) {
        setProgressMessage('Analisi ed elaborazione metriche...');
      } else if (p < 75) {
        setProgressMessage('Generazione documento e formattazione...');
      } else if (p < 100) {
        setProgressMessage('Finalizzazione report...');
      }
      if (p >= 100) {
        if (timerRef.current) clearInterval(timerRef.current);
        setIsGenerating(false);
        setIsComplete(true);
        setProgressMessage('Report generato con successo!');
      }
    }, interval);
  };
  const currentThemes = THEMES_BY_TYPE[selectedType] || [];
  const stepLabels = ['Tipo', 'Configurazione', 'Formato', 'Generazione'];
  return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8" data-id="element-4398">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8" data-id="element-4399">
        <h1 className="text-[28px] md:text-[32px] font-semibold text-[#1A202C] tracking-tight" data-id="element-4400">
          Report
        </h1>
        <button onClick={handleOpenWizard} className="flex items-center gap-2 px-4 py-2.5 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors shadow-sm" data-id="element-4401">
          <Plus className="w-4 h-4" data-id="element-4402" />
          <span className="text-[13px] font-medium" data-id="element-4403">Genera Report</span>
        </button>
      </div>

      {/* Report Table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-4404">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-4405">
          <h2 className="text-[15px] font-semibold text-[#1A202C]" data-id="element-4406">
            Report Generati
          </h2>
          <span className="text-[12px] text-gray-500" data-id="element-4407">
            {reports.length} report
          </span>
        </div>
        <div className="overflow-x-auto" data-id="element-4408">
          <table className="w-full" data-id="element-4409">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-4410">
              <tr data-id="element-4411">
                <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4412">
                  Nome Report
                </th>
                <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4413">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4414">
                  Data Creazione
                </th>
                <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4415">
                  Periodo
                </th>
                <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4416">
                  Formato
                </th>
                <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4417">
                  Stato
                </th>
                <th className="px-6 py-3 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4418">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-4419">
              {reports.map(report => <tr key={report.id} className="hover:bg-gray-50 transition-colors" data-id="element-4420">
                  <td className="px-6 py-4" data-id="element-4421">
                    <div className="flex items-center gap-2.5" data-id="element-4422">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-4423" />
                      <span className="text-[13px] font-medium text-[#1A202C]" data-id="element-4424">
                        {report.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4425">{getTypeBadge(report.type)}</td>
                  <td className="px-6 py-4 text-[12px] text-gray-600" data-id="element-4426">
                    {report.date}
                  </td>
                  <td className="px-6 py-4 text-[12px] text-gray-600" data-id="element-4427">
                    {report.period}
                  </td>
                  <td className="px-6 py-4" data-id="element-4428">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-700" data-id="element-4429">
                      {report.format}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-4430">{getStatusBadge(report.status)}</td>
                  <td className="px-6 py-4" data-id="element-4431">
                    <div className="flex items-center justify-end gap-1" data-id="element-4432">
                      <button title="Scarica" className="p-1.5 text-gray-400 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-all" data-id="element-4433">
                        <Download className="w-4 h-4" data-id="element-4434" />
                      </button>
                      <button title="Visualizza" onClick={() => setPreviewReport(report)} className="p-1.5 text-gray-400 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-all" data-id="element-4435">
                        <Eye className="w-4 h-4" data-id="element-4436" />
                      </button>
                      <button title="Elimina" className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" data-id="element-4437">
                        <Trash2 className="w-4 h-4" data-id="element-4438" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== REPORT PREVIEW MODAL ===== */}
      {previewReport && <ReportPreviewModal report={previewReport} onClose={() => setPreviewReport(null)} data-id="element-4439" />}

      {/* ===== WIZARD MODAL ===== */}
      {isWizardOpen && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-4440">
          <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" data-id="element-4441">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between" data-id="element-4442">
              <div data-id="element-4443">
                <h2 className="text-[17px] font-heading font-semibold text-[#1A202C]" data-id="element-4444">
                  Genera Nuovo Report
                </h2>
                <p className="text-[12px] text-gray-500 mt-0.5" data-id="element-4445">
                  Step {wizardStep} di 4 — {stepLabels[wizardStep - 1]}
                </p>
              </div>
              <button onClick={() => setIsWizardOpen(false)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all" data-id="element-4446">
                <X className="w-5 h-5" data-id="element-4447" />
              </button>
            </div>

            {/* Step Indicator */}
            <div className="px-6 pt-4 pb-2" data-id="element-4448">
              <div className="flex items-center gap-2" data-id="element-4449">
                {stepLabels.map((label, i) => <Fragment key={label} data-id="element-4450">
                    <div className="flex items-center gap-2" data-id="element-4451">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${i + 1 < wizardStep ? 'bg-emerald-500 text-white' : i + 1 === wizardStep ? 'bg-[#00B4D8] text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-4452">
                        {i + 1 < wizardStep ? <CheckCircle2 className="w-4 h-4" data-id="element-4453" /> : i + 1}
                      </div>
                      <span className={`text-[12px] font-medium hidden sm:inline ${i + 1 === wizardStep ? 'text-[#1A202C]' : 'text-gray-400'}`} data-id="element-4454">
                        {label}
                      </span>
                    </div>
                    {i < stepLabels.length - 1 && <div className={`flex-1 h-px ${i + 1 < wizardStep ? 'bg-emerald-300' : 'bg-gray-200'}`} data-id="element-4455" />}
                  </Fragment>)}
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-4456">
              {/* STEP 1: Report Type */}
              {wizardStep === 1 && <div data-id="element-4457">
                  <h3 className="text-[14px] font-semibold text-[#1A202C] mb-1" data-id="element-4458">
                    Seleziona il tipo di report
                  </h3>
                  <p className="text-[12px] text-gray-500 mb-5" data-id="element-4459">
                    Scegli la categoria di report che desideri generare.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-4460">
                    {REPORT_TYPES.map(rt => {
                const Icon = rt.icon;
                const isSelected = selectedType === rt.id;
                return <button key={rt.id} onClick={() => {
                  setSelectedType(rt.id);
                  const defaults = THEMES_BY_TYPE[rt.id] || [];
                  setSelectedThemes(defaults.slice(0, 2));
                }} className={`text-left p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-[#00B4D8] bg-[#00B4D8]/5 shadow-sm' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-4461">
                          <div className="flex items-center gap-2.5 mb-2" data-id="element-4462">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? 'bg-[#00B4D8]/10' : 'bg-gray-100'}`} data-id="element-4463">
                              <Icon className={`w-4 h-4 ${isSelected ? 'text-[#00B4D8]' : 'text-gray-500'}`} data-id="element-4464" />
                            </div>
                            <span className="text-[13px] font-semibold text-[#1A202C]" data-id="element-4465">
                              {rt.title}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-500 leading-relaxed" data-id="element-4466">
                            {rt.desc}
                          </p>
                        </button>;
              })}
                  </div>
                </div>}

              {/* STEP 2: Configuration */}
              {wizardStep === 2 && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4467">
                  {/* Left: Themes */}
                  <div data-id="element-4468">
                    <h3 className="text-[14px] font-semibold text-[#1A202C] mb-1" data-id="element-4469">
                      Temi e obiettivi
                    </h3>
                    <p className="text-[12px] text-gray-500 mb-4" data-id="element-4470">
                      Seleziona gli argomenti da includere nel report.
                    </p>
                    <div className="space-y-2" data-id="element-4471">
                      {currentThemes.map(theme => <label key={theme} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selectedThemes.includes(theme) ? 'border-[#00B4D8] bg-[#00B4D8]/5' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4472">
                          <input type="checkbox" checked={selectedThemes.includes(theme)} onChange={() => toggleTheme(theme)} className="w-4 h-4 rounded border-gray-300 text-[#00B4D8] focus:ring-[#00B4D8]" data-id="element-4473" />
                          <span className="text-[13px] text-[#1A202C] font-medium" data-id="element-4474">
                            {theme}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  {/* Right: Filters */}
                  <div className="space-y-5" data-id="element-4475">
                    <div data-id="element-4476">
                      <h3 className="text-[14px] font-semibold text-[#1A202C] mb-1" data-id="element-4477">
                        Periodo
                      </h3>
                      <p className="text-[12px] text-gray-500 mb-3" data-id="element-4478">
                        Intervallo temporale del report.
                      </p>
                      <div className="grid grid-cols-2 gap-3" data-id="element-4479">
                        <div data-id="element-4480">
                          <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-4481">
                            Da
                          </label>
                          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-[12px] text-[#1A202C] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-4482" />
                        </div>
                        <div data-id="element-4483">
                          <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-4484">
                            A
                          </label>
                          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-[12px] text-[#1A202C] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-4485" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-4486">
                      <h3 className="text-[14px] font-semibold text-[#1A202C] mb-1" data-id="element-4487">
                        Servizi da includere
                      </h3>
                      <p className="text-[12px] text-gray-500 mb-3" data-id="element-4488">
                        Seleziona i servizi AI da analizzare.
                      </p>
                      <div className="space-y-2" data-id="element-4489">
                        {SERVICES.map(service => <label key={service} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${selectedServices.includes(service) ? 'border-[#00B4D8] bg-[#00B4D8]/5' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4490">
                            <input type="checkbox" checked={selectedServices.includes(service)} onChange={() => toggleService(service)} className="w-4 h-4 rounded border-gray-300 text-[#00B4D8] focus:ring-[#00B4D8]" data-id="element-4491" />
                            <span className="text-[13px] text-[#1A202C]" data-id="element-4492">
                              {service}
                            </span>
                          </label>)}
                      </div>
                    </div>

                    <div data-id="element-4493">
                      <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-4494">
                        Note aggiuntive (opzionale)
                      </label>
                      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Aggiungi eventuali note o richieste specifiche..." className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-[12px] text-[#1A202C] placeholder-gray-400 focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8] resize-none" data-id="element-4495" />
                    </div>
                  </div>
                </div>}

              {/* STEP 3: Format & Summary */}
              {wizardStep === 3 && <div data-id="element-4496">
                  <h3 className="text-[14px] font-semibold text-[#1A202C] mb-1" data-id="element-4497">
                    Formato di esportazione
                  </h3>
                  <p className="text-[12px] text-gray-500 mb-5" data-id="element-4498">
                    Scegli il formato in cui generare il report.
                  </p>

                  <div className="grid grid-cols-3 gap-3 mb-8" data-id="element-4499">
                    {FORMAT_OPTIONS.map(fmt => {
                const Icon = fmt.icon;
                const isSelected = selectedFormat === fmt.id;
                return <button key={fmt.id} onClick={() => setSelectedFormat(fmt.id)} className={`text-left p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-[#00B4D8] bg-[#00B4D8]/5' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4500">
                          <Icon className={`w-6 h-6 mb-2 ${isSelected ? 'text-[#00B4D8]' : 'text-gray-400'}`} data-id="element-4501" />
                          <p className="text-[13px] font-semibold text-[#1A202C]" data-id="element-4502">
                            {fmt.label}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-4503">
                            {fmt.desc}
                          </p>
                        </button>;
              })}
                  </div>

                  {/* Summary */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-5" data-id="element-4504">
                    <h4 className="text-[13px] font-semibold text-[#1A202C] mb-4" data-id="element-4505">
                      Riepilogo Configurazione
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-[12px]" data-id="element-4506">
                      <div data-id="element-4507">
                        <span className="text-gray-500" data-id="element-4508">Tipo Report</span>
                        <p className="font-medium text-[#1A202C] mt-0.5" data-id="element-4509">
                          {selectedType}
                        </p>
                      </div>
                      <div data-id="element-4510">
                        <span className="text-gray-500" data-id="element-4511">Formato</span>
                        <p className="font-medium text-[#1A202C] mt-0.5" data-id="element-4512">
                          {selectedFormat}
                        </p>
                      </div>
                      <div data-id="element-4513">
                        <span className="text-gray-500" data-id="element-4514">Periodo</span>
                        <p className="font-medium text-[#1A202C] mt-0.5" data-id="element-4515">
                          {dateFrom && dateTo ? `${dateFrom} — ${dateTo}` : 'Mese corrente'}
                        </p>
                      </div>
                      <div data-id="element-4516">
                        <span className="text-gray-500" data-id="element-4517">Servizi</span>
                        <p className="font-medium text-[#1A202C] mt-0.5" data-id="element-4518">
                          {selectedServices.length === SERVICES.length ? 'Tutti i servizi' : `${selectedServices.length} servizi`}
                        </p>
                      </div>
                      <div className="col-span-2" data-id="element-4519">
                        <span className="text-gray-500" data-id="element-4520">Temi selezionati</span>
                        <div className="flex flex-wrap gap-1.5 mt-1.5" data-id="element-4521">
                          {selectedThemes.map(t => <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#00B4D8]/10 text-[#00B4D8] border border-[#00B4D8]/20" data-id="element-4522">
                              {t}
                            </span>)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}

              {/* STEP 4: Generation */}
              {wizardStep === 4 && <div className="flex flex-col items-center justify-center py-10" data-id="element-4523">
                  {!isComplete ? <>
                      <div className="w-16 h-16 rounded-full bg-[#00B4D8]/10 flex items-center justify-center mb-6" data-id="element-4524">
                        <Loader2 className="w-8 h-8 text-[#00B4D8] animate-spin" data-id="element-4525" />
                      </div>
                      <h3 className="text-[16px] font-semibold text-[#1A202C] mb-2" data-id="element-4526">
                        Generazione in corso...
                      </h3>
                      <p className="text-[13px] text-gray-500 mb-6" data-id="element-4527">
                        {progressMessage}
                      </p>
                      <div className="w-full max-w-md" data-id="element-4528">
                        <div className="flex items-center justify-between mb-2" data-id="element-4529">
                          <span className="text-[11px] text-gray-500" data-id="element-4530">
                            Progresso
                          </span>
                          <span className="text-[11px] font-medium text-[#1A202C]" data-id="element-4531">
                            {progress}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-4532">
                          <div className="bg-[#00B4D8] h-2 rounded-full transition-all duration-100" style={{
                    width: `${progress}%`
                  }} data-id="element-4533" />
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-6 text-[11px] text-gray-400" data-id="element-4534">
                        <span className="flex items-center gap-1" data-id="element-4535">
                          <Clock className="w-3 h-3" data-id="element-4536" />
                          Tempo stimato: ~30s
                        </span>
                        <span data-id="element-4537">Report: {selectedType}</span>
                        <span data-id="element-4538">Formato: {selectedFormat}</span>
                      </div>
                    </> : <>
                      <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-6" data-id="element-4539">
                        <CheckCircle2 className="w-8 h-8 text-emerald-500" data-id="element-4540" />
                      </div>
                      <h3 className="text-[16px] font-semibold text-[#1A202C] mb-2" data-id="element-4541">
                        Report generato con successo!
                      </h3>
                      <p className="text-[13px] text-gray-500 mb-6" data-id="element-4542">
                        Il report {selectedType} in formato {selectedFormat} è
                        pronto per il download.
                      </p>
                      <div className="flex items-center gap-3" data-id="element-4543">
                        <button className="flex items-center gap-2 px-5 py-2.5 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors" data-id="element-4544">
                          <Download className="w-4 h-4" data-id="element-4545" />
                          <span className="text-[13px] font-medium" data-id="element-4546">
                            Scarica Report
                          </span>
                        </button>
                        <button onClick={handleCloseWizard} className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-4547">
                          <span className="text-[13px] font-medium" data-id="element-4548">
                            Chiudi
                          </span>
                        </button>
                      </div>
                    </>}
                </div>}
            </div>

            {/* Modal Footer */}
            {wizardStep < 4 && <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-4549">
                <button onClick={() => {
            if (wizardStep === 1) {
              setIsWizardOpen(false);
            } else {
              setWizardStep(s => s - 1);
            }
          }} className="flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-medium text-gray-600 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-all" data-id="element-4550">
                  <ChevronLeft className="w-4 h-4" data-id="element-4551" />
                  {wizardStep === 1 ? 'Annulla' : 'Indietro'}
                </button>

                {wizardStep < 3 ? <button onClick={() => setWizardStep(s => s + 1)} className="flex items-center gap-1.5 px-5 py-2.5 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors text-[13px] font-medium" data-id="element-4552">
                    Avanti
                    <ChevronRight className="w-4 h-4" data-id="element-4553" />
                  </button> : <button onClick={startGeneration} className="flex items-center gap-2 px-5 py-2.5 bg-[#00B4D8] text-white rounded-lg hover:bg-[#009BB8] transition-colors text-[13px] font-medium" data-id="element-4554">
                    <FileText className="w-4 h-4" data-id="element-4555" />
                    Genera Report
                  </button>}
              </div>}
          </div>
        </div>}
    </div>;
}